import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import MissionFile from "../components/MissionFile.jsx";

// /admin/participant/:id, full dossier + per-participant tailoring.
export default function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = () => api.adminParticipant(id).then(setD).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [id]);

  if (error) return <Shell><p className="qf-lead">{error}</p></Shell>;
  if (!d) return <Shell><p className="muted-sm">Loading…</p></Shell>;
  const p = d.participant;

  return (
    <Shell>
      <Link className="link-btn" to="/admin">← All participants</Link>
      <div className="hstack qf-between">
        <div>
          <h1 className="qf-title">{p.full_name}</h1>
          <p className="muted-sm">{p.email}{p.sector ? ` · ${p.sector}` : ""} · {p.status.replace("_"," ")} · {p.overall_progress_pct}% · {p.peer_respondent_count} peer responses</p>
        </div>
        <div className="hstack">
          <a className="qf-btn qf-btn--primary" href={`/api/admin/bundle?id=${id}`}>Download bundle .zip</a>
          <TranscribeButton id={id} onDone={load} />
          <a className="qf-btn qf-btn--ghost" href={`/api/admin/export?id=${id}&format=md`}>.md</a>
          <a className="qf-btn qf-btn--ghost" href={`/api/admin/export?id=${id}&format=json`}>JSON</a>
        </div>
      </div>

      {/* Fit summary */}
      <div className="qf-card">
        <div className="qf-label qf-label--accent">Fit summary</div>
        <div className="qf-kv" style={{ marginTop: "var(--qf-3)" }}>
          <div className="qf-kv__row"><span className="qf-kv__k">Weekly time</span><span className="qf-kv__v">{d.fit.weekly_time}</span></div>
          <div className="qf-kv__row"><span className="qf-kv__k">Working mode</span><span className="qf-kv__v">{d.fit.working_mode}</span></div>
          <div className="qf-kv__row"><span className="qf-kv__k">Investment appetite</span><span className="qf-kv__v">{d.fit.investment_appetite}</span></div>
        </div>
      </div>

      {/* Sections A and B: the living mission file, published item by item */}
      <MissionFile participantId={id} />

      {/* Post-data audit */}
      <AuditPanel id={id} audit={d.audit} custom={d.custom} onChange={load} />

      {/* Contradiction flags */}
      {d.flags.length > 0 && (
        <div className="qf-card">
          <div className="qf-label qf-label--accent">Flags to review</div>
          <ul style={{ marginTop: "var(--qf-3)" }}>
            {d.flags.map((f, i) => <li key={i}><span className="qf-flag" style={{ marginRight: ".5rem" }}>{f.level}</span>{f.text}</li>)}
          </ul>
        </div>
      )}

      {/* Ratings */}
      {d.ratings.list.length > 0 && (
        <div className="qf-card">
          <div className="qf-label qf-label--accent">"Sounds like me" ratings</div>
          <div className="hstack" style={{ marginTop: "var(--qf-3)" }}>
            {Object.entries(d.ratings.counts).map(([k, v]) => <span key={k} className="qf-pill"><strong>{v}</strong> {k}</span>)}
          </div>
          {d.ratings.list.filter((r) => r.note).length > 0 && (
            <div className="qf-stack" style={{ marginTop: "var(--qf-4)" }}>
              {d.ratings.list.filter((r) => r.note).map((r, i) => (
                <div key={i}>
                  {r.body && <div className="muted-sm" style={{ fontStyle: "italic" }}>“{r.body}”</div>}
                  <div><span className="qf-flag" style={{ marginRight: ".5rem" }}>{r.label}</span>{r.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quests */}
      {d.quests.map((q) => (
        <div key={q.quest_key} className="qf-card">
          <div className="qf-label qf-label--accent">{q.name}</div>
          <div className="qf-stack" style={{ marginTop: "var(--qf-4)" }}>
            {q.questions.map((item) => <DossierItem key={item.question_key} item={item} token="" />)}
          </div>
          <Tailor participantId={id} questKey={q.quest_key} questions={q.questions}
            custom={d.custom.filter((c) => c.quest_key === q.quest_key)} onChange={load} />
        </div>
      ))}

      {/* Tailoring whose quest no longer renders — e.g. every question in it is hidden.
          Without this the rows would be unreachable and the hide impossible to undo. */}
      <OrphanTailoring custom={d.custom} questKeys={d.quests.map((q) => q.quest_key)} onChange={load} />

      {/* Peers */}
      {d.peers.length > 0 && (
        <div className="qf-card">
          <div className="qf-label qf-label--accent">Mirror, how others see them</div>
          {d.peers.map((person, i) => (
            <div key={i} style={{ marginTop: "var(--qf-4)" }}>
              <div style={{ fontFamily: "var(--qf-serif)", fontSize: "1.1rem" }}>{person.name} <span className="muted-sm">{person.email}</span></div>
              {person.answers.map((a, j) => (
                <div key={j} style={{ marginTop: "var(--qf-3)" }}>
                  <div className="muted-sm">{a.prompt}</div>
                  {a.value_text && <p style={{ whiteSpace: "pre-wrap" }}>{a.value_text}</p>}
                  {a.transcript_text && <p><em>Transcript:</em> {a.transcript_text}</p>}
                  {a.audio_url && <audio controls src={a.audio_url} />}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <DeleteZone name={p.full_name} onDelete={async () => { await api.adminDeleteParticipant(id); navigate("/admin"); }} />
    </Shell>
  );
}

// GDPR / cleanup: erase a participant and everything tied to them.
function DeleteZone({ name, onDelete }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="dangerzone">
      <div>
        <div className="qf-label" style={{ color: "#b23" }}>Delete this person</div>
        <p className="muted-sm" style={{ marginTop: ".3rem" }}>Removes {name}'s answers, files, audio and links. Permanent, can't be undone.</p>
      </div>
      {!armed
        ? <button className="qf-btn qf-btn--ghost danger" onClick={() => setArmed(true)}>Delete…</button>
        : (
          <div className="hstack">
            <button className="qf-btn danger-solid" disabled={busy} onClick={async () => { setBusy(true); try { await onDelete(); } finally { setBusy(false); } }}>
              {busy ? "Deleting…" : "Yes, delete permanently"}
            </button>
            <button className="subtle" onClick={() => setArmed(false)}>Cancel</button>
          </div>
        )}
    </div>
  );
}

function DossierItem({ item }) {
  const j = item.value_json || {};
  return (
    <div>
      <div className="hstack qf-between">
        <div style={{ fontFamily: "var(--qf-serif)", fontSize: "1.05rem" }}>{item.prompt}</div>
        <span className={`state-tag state-tag--${item.state}`}>{item.state}</span>
      </div>
      <div className="muted-sm">lever: {item.lever || "not set"}{item.deep ? " · deep" : ""}</div>
      {(item.annotations || []).map((a, i) => <div className="qf-note" key={i} style={{ marginTop: ".4rem" }}>Audit note: {a}</div>)}

      {item.value_text && <p style={{ whiteSpace: "pre-wrap" }}>{item.value_text}</p>}
      {item.transcript_text && <p><em>Transcript:</em> {item.transcript_text}</p>}
      {j.choice && <p>{j.choice}</p>}
      {j.value != null && <p>{j.value}/100</p>}
      {Array.isArray(j.selected) && <p>{[...j.selected, j.other].filter(Boolean).join(", ")}</p>}
      {j.text && <p>{j.text}</p>}
      {Array.isArray(j.pairs) && j.pairs.map((p, i) => <p key={i}>{p.left} → {p.right}</p>)}
      {Array.isArray(j.topics) && j.topics.map((t, i) => <p key={i}>{t.topic}, knows {t.a}/100, comfort {t.b}/100</p>)}
      {Array.isArray(j.items) && j.items.map((it, i) => (
        <p key={i}>
          {it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.url}</a> : ""}
          {it.why && `, ${it.why}`}
          {it.borrow && <> · borrow: {it.borrow}</>}
          {it.avoid && <> · avoid: {it.avoid}</>}
        </p>
      ))}
      {(j.type) && <p>{j.type}{j.suffix ? `-${j.suffix}` : ""} {j.url && <a href={j.url} target="_blank" rel="noreferrer">(result)</a>}</p>}

      {(item.assets || []).map((a) => (
        <div key={a.id} style={{ marginTop: ".4rem" }}>
          {a.kind === "audio" && <audio controls src={a.media_url} />}
          {a.kind === "link" && <a href={a.external_url} target="_blank" rel="noreferrer">{a.title || a.external_url}</a>}
          {a.kind === "file" && <a href={a.media_url} target="_blank" rel="noreferrer">{a.title || a.mime || "file"}</a>}
        </div>
      ))}
    </div>
  );
}

// Upload a LinkedIn activity export; show the computed insights + AI layer.
function AuditPanel({ id, audit, custom, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ins = audit?.insights;

  const onFile = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setErr(null); setBusy(true);
    try {
      const csv = await f.text();
      await api.adminUploadAudit({ participant_id: id, csv });
      onChange();
    } catch (ex) { setErr(ex.message || "Upload failed."); }
    finally { setBusy(false); }
  };
  const remove = async () => { setBusy(true); try { await api.adminDeleteAudit(id); onChange(); } finally { setBusy(false); } };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "—";

  return (
    <div className="qf-card">
      <div className="hstack qf-between">
        <div className="qf-label qf-label--accent">Post data</div>
        {ins && <button className="link-btn" onClick={remove} disabled={busy}>remove</button>}
      </div>

      {!ins ? (
        <div style={{ marginTop: "var(--qf-3)" }}>
          <p className="muted-sm">Upload their LinkedIn activity export (.csv). It powers the “what your data shows” panel and auto-adapts a few questions.</p>
          <div style={{ marginTop: ".5rem" }}>
            <label className="filepick">
              <span className="qf-btn qf-btn--ghost">{busy ? "Reading…" : "Choose a .csv file"}</span>
              <input type="file" accept=".csv,text/csv" onChange={onFile} />
            </label>
          </div>
          {err && <p className="field-err" style={{ marginTop: ".4rem" }}>{err}</p>}
        </div>
      ) : (
        <>
          <div className="qf-kv" style={{ marginTop: "var(--qf-3)" }}>
            <div className="qf-kv__row"><span className="qf-kv__k">Original posts</span><span className="qf-kv__v">{ins.own_count}{ins.repost_count ? ` · ${ins.repost_count} reposts` : ""}</span></div>
            <div className="qf-kv__row"><span className="qf-kv__k">Cadence</span><span className="qf-kv__v">{ins.per_month != null ? `${ins.per_month} / month` : "—"} <span className="muted-sm">({fmtDate(ins.first_date)} → {fmtDate(ins.last_date)})</span></span></div>
            <div className="qf-kv__row"><span className="qf-kv__k">Avg engagement</span><span className="qf-kv__v">{ins.avg_eng} <span className="muted-sm">({ins.avg_likes} likes, {ins.avg_comments} comments, {ins.avg_reposts} reposts)</span></span></div>
            <div className="qf-kv__row"><span className="qf-kv__k">Formats</span><span className="qf-kv__v">{Object.entries(ins.formats || {}).map(([k, v]) => `${k} ×${v}`).join(", ")}{ins.best_format ? ` · best: ${ins.best_format}` : ""}</span></div>
            {ins.best_dow && <div className="qf-kv__row"><span className="qf-kv__k">Most active day</span><span className="qf-kv__v">{ins.best_dow.day}</span></div>}
            {ins.avg_words != null && <div className="qf-kv__row"><span className="qf-kv__k">Avg length</span><span className="qf-kv__v">{ins.avg_words} words</span></div>}
            {ins.top_terms?.length > 0 && <div className="qf-kv__row"><span className="qf-kv__k">Recurring terms</span><span className="qf-kv__v">{ins.top_terms.map((t) => `${t.term} (${t.n})`).join(" · ")}</span></div>}
            {ins.hashtags?.length > 0 && <div className="qf-kv__row"><span className="qf-kv__k">Hashtags</span><span className="qf-kv__v">{ins.hashtags.map((h) => h.tag).join(" ")}</span></div>}
            {ins.top_post && <div className="qf-kv__row"><span className="qf-kv__k">Top post</span><span className="qf-kv__v">“{ins.top_post.text.slice(0, 90)}…” <span className="muted-sm">({ins.top_post.eng})</span></span></div>}
          </div>
          <AiLayer id={id} audit={audit} custom={custom} onChange={onChange} />
        </>
      )}
    </div>
  );
}

// AI layer: analyse posts + profile with Claude, then the operator reviews and
// publishes only what they approve. Nothing here reaches the participant until published.
const BRIEF_SCHEMA = `{
  "themes": [{"label": "...", "evidence": "verbatim quote copied from a post"}],
  "tone": "one sentence on their writing tone",
  "angles": [{"label": "...", "evidence": "verbatim quote from a post"}],
  "profile": {"languages": [], "sector": "", "role": "", "company": "", "summary": ""},
  "suggested_prefills": [{"question_key": "q1_describe|q4_need|q5_who|q6_pillar|q6_openlane|q8_data", "draft": "first-person draft answer"}]
}`;

function buildBrief(posts, profileText) {
  const postsBlock = (posts || []).map((p, i) => `--- Post ${i + 1} (${p.type}, ${p.eng} interactions) ---\n${p.text}`).join("\n\n");
  return `You analyse a person's OWN LinkedIn posts (and profile if given) to help their content strategist. Be rigorous and strictly evidence-bound.

Rules:
1. Use ONLY what is explicitly in the material below. Never infer beyond the text.
2. Every "theme" and "angle" MUST include an "evidence" field: a short quote copied VERBATIM from one of the posts.
3. If you cannot ground a claim in a real quote, omit it. Fewer solid items beat many weak ones.
4. Write "label" and "draft" in the same language the person writes in.
5. Reply with ONLY a valid JSON object matching this schema exactly, no text around it:

${BRIEF_SCHEMA}

=== POSTS ===
${postsBlock}
${profileText ? `\n=== PROFILE ===\n${profileText}\n\n(You can also attach their profile PDF directly here in the chat.)` : `\n(Optionally paste or attach their LinkedIn profile too, to fill "profile" and ground "suggested_prefills".)`}`;
}

function AiLayer({ id, audit, custom, onChange }) {
  const [busy, setBusy] = useState(false);
  const [addingIdx, setAddingIdx] = useState(null);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const [profileText, setProfileText] = useState(audit.profile_text || "");
  const [pdfB64, setPdfB64] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [briefCopied, setBriefCopied] = useState(false);
  const [paste, setPaste] = useState("");
  const raw = audit.ai_raw;
  const published = audit.ai_published;
  const hasApiKey = false; // Anthropic API path (kept for later); manual paste is the default.
  // Which themes are ticked for publication (default: whatever's already published, else all).
  const [picked, setPicked] = useState(null);
  const themeKey = (t) => t.label;
  const pickedSet = picked ?? new Set((published?.themes || raw?.themes || []).map(themeKey));

  const readPdf = (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    setPdfName(f.name);
    const r = new FileReader();
    r.onload = () => setPdfB64(String(r.result).split(",")[1] || null);
    r.readAsDataURL(f);
  };

  const analyze = async () => {
    setErr(null); setBusy(true);
    try {
      await api.adminAnalyze({ participant_id: id, profile_text: profileText || undefined, profile_pdf_base64: pdfB64 || undefined });
      setPicked(null); onChange();
    } catch (ex) { setErr(ex.message || "Analysis failed."); }
    finally { setBusy(false); }
  };

  const copyBrief = async () => {
    await navigator.clipboard.writeText(buildBrief(audit.posts, profileText));
    setBriefCopied(true); setTimeout(() => setBriefCopied(false), 1800);
  };

  const importPaste = async () => {
    setErr(null); setNote(null); setBusy(true);
    try {
      const res = await api.adminImportAnalysis({ participant_id: id, ai: paste });
      setPaste(""); setPicked(null);
      setNote(res.dropped ? `Loaded. ${res.dropped} item(s) dropped: their quote wasn't found in the posts.` : "Loaded and every citation checked out.");
      onChange();
    } catch (ex) { setErr(ex.message || "Could not load that."); }
    finally { setBusy(false); }
  };

  const toggle = (t) => {
    const s = new Set(pickedSet);
    s.has(themeKey(t)) ? s.delete(themeKey(t)) : s.add(themeKey(t));
    setPicked(s);
  };

  const publish = async () => {
    setBusy(true);
    try {
      const themes = (raw.themes || []).filter((t) => pickedSet.has(themeKey(t))).map((t) => ({ label: t.label }));
      await api.adminPublishAudit({ participant_id: id, ai_published: themes.length ? { themes } : null });
      onChange();
    } finally { setBusy(false); }
  };

  // A suggested draft is "added" if a matching prefill already exists on the participant.
  const isAdded = (p) => (custom || []).some((c) => c.kind === "prefill" && c.anchor_question_key === p.question_key && c.prompt_or_note === p.draft);
  const addPrefill = async (p, i) => {
    setAddingIdx(i);
    try {
      await api.adminTailor({ participant_id: id, quest_key: p.question_key.split("_")[0], kind: "prefill", anchor_question_key: p.question_key, prompt_or_note: p.draft });
      onChange();
    } finally { setAddingIdx(null); }
  };

  return (
    <div style={{ marginTop: "var(--qf-4)", paddingTop: "var(--qf-4)", borderTop: "1px solid var(--qf-hairline)" }}>
      <div className="hstack qf-between">
        <div className="qf-label">AI analysis <span className="muted-sm">reviewed by you before the participant sees anything</span></div>
      </div>

      {/* Manual route: run it in your own Claude account, no API/billing needed. */}
      <div className="qf-stack" style={{ marginTop: "var(--qf-3)" }}>
        <p className="muted-sm">Run this in your Claude account: copy the brief, paste it into claude.ai (attach their profile PDF there if you have it), then paste Claude's JSON reply back below. Every citation is re-checked against the real posts on load.</p>
        <textarea className="input" placeholder="Optional: paste their LinkedIn profile text to include in the brief…" value={profileText}
          onChange={(e) => setProfileText(e.target.value)} style={{ minHeight: 60 }} />
        <div className="hstack">
          <button className="qf-btn qf-btn--ghost" onClick={copyBrief}>{briefCopied ? "Copied ✓" : "1 · Copy the brief"}</button>
          {hasApiKey && <button className="qf-btn qf-btn--ghost" onClick={analyze} disabled={busy}>{busy ? "Analysing…" : "or analyse via API"}</button>}
        </div>
        <textarea className="input" placeholder="2 · Paste Claude's JSON reply here…" value={paste}
          onChange={(e) => setPaste(e.target.value)} style={{ minHeight: 90 }} />
        <div className="hstack">
          <button className="qf-btn qf-btn--primary" onClick={importPaste} disabled={busy || !paste.trim()}>{busy ? "Loading…" : "3 · Load analysis"}</button>
        </div>
        {note && <p className="muted-sm">{note}</p>}
        {err && <p className="field-err">{err}</p>}
      </div>

      {raw && (
        <div className="qf-stack" style={{ marginTop: "var(--qf-4)" }}>
          {/* Themes → publishable */}
          <div>
            <div className="qf-label qf-label--accent">Themes <span className="muted-sm">tick what the participant should see</span></div>
            <div className="qf-stack" style={{ marginTop: ".5rem" }}>
              {(raw.themes || []).map((t, i) => (
                <label key={i} className="hstack" style={{ alignItems: "flex-start", gap: ".5rem" }}>
                  <input type="checkbox" checked={pickedSet.has(themeKey(t))} onChange={() => toggle(t)} style={{ marginTop: ".3rem" }} />
                  <span><strong>{t.label}</strong><br /><span className="muted-sm" style={{ fontStyle: "italic" }}>“{t.evidence}”</span></span>
                </label>
              ))}
            </div>
            <div className="hstack" style={{ marginTop: ".6rem" }}>
              <button className="qf-btn qf-btn--primary" onClick={publish} disabled={busy}>Publish to participant</button>
              {published?.themes?.length ? <span className="muted-sm">{published.themes.length} theme(s) live</span> : <span className="muted-sm">nothing published yet</span>}
            </div>
          </div>

          {raw.tone && <div><div className="qf-label">Tone</div><p className="muted-sm">{raw.tone}</p></div>}

          {raw.angles?.length > 0 && (
            <div>
              <div className="qf-label">Angles that work</div>
              {raw.angles.map((a, i) => <div key={i} className="muted-sm" style={{ marginTop: ".2rem" }}><strong>{a.label}</strong> — “{a.evidence}”</div>)}
            </div>
          )}

          {raw.profile && (
            <div>
              <div className="qf-label">Profile</div>
              <div className="muted-sm" style={{ marginTop: ".2rem" }}>
                {raw.profile.role && <>{raw.profile.role}{raw.profile.company ? ` · ${raw.profile.company}` : ""}<br /></>}
                {raw.profile.sector && <>Sector: {raw.profile.sector}<br /></>}
                {raw.profile.languages?.length > 0 && <>Languages: {raw.profile.languages.join(", ")}<br /></>}
                {raw.profile.summary && <>{raw.profile.summary}</>}
              </div>
            </div>
          )}

          {raw.suggested_prefills?.length > 0 && (
            <div>
              <div className="qf-label qf-label--accent">Suggested draft answers</div>
              <div className="qf-stack" style={{ marginTop: ".5rem" }}>
                {raw.suggested_prefills.map((p, i) => {
                  const added = isAdded(p);
                  return (
                    <div key={i} className="qf-card qf-card--quiet">
                      <div className="muted-sm">{p.question_key}</div>
                      <p style={{ margin: ".2rem 0" }}>{p.draft}</p>
                      {added
                        ? <span className="added-chip">✓ Added as draft answer</span>
                        : <button className="link-btn" onClick={() => addPrefill(p, i)} disabled={addingIdx !== null}>
                            {addingIdx === i ? "Adding…" : "+ add as draft answer"}
                          </button>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Add bespoke questions or audit annotations to this participant.
function Tailor({ participantId, questKey, questions, custom, onChange }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("annotation");
  const [anchor, setAnchor] = useState(questions[0]?.question_key || "");
  const [text, setText] = useState("");
  const [type, setType] = useState("text");

  const anchored = kind === "annotation" || kind === "prefill" || kind === "hide";
  // A hide needs no text: the anchor is the instruction. The note is just the reason,
  // which is what tells you six weeks later why the question went away.
  const add = async () => {
    if (kind !== "hide" && !text.trim()) return;
    await api.adminTailor({
      participant_id: participantId, quest_key: questKey, kind,
      prompt_or_note: text.trim() || undefined,
      anchor_question_key: anchored ? anchor : null,
      type: kind === "extra_question" ? type : null,
    });
    setText(""); onChange();
  };
  const kindGlyph = { annotation: "📎 note", prefill: "✎ draft", extra_question: "＋ question", hide: "🚫 hidden" };
  const promptOf = (key) => questions.find((q) => q.question_key === key)?.prompt || key;

  return (
    <div style={{ marginTop: "var(--qf-4)" }}>
      {custom.length > 0 && (
        <div className="muted-sm">
          {custom.map((c) => (
            <div key={c.id} className="hstack qf-between" style={{ padding: ".3rem 0" }}>
              <span>
                {kindGlyph[c.kind] || c.kind}
                {c.anchor_question_key ? ` · ${c.anchor_question_key}` : ""}: {c.prompt_or_note}
              </span>
              <button className="link-btn" onClick={async () => { await api.adminUntailor(c.id); onChange(); }}>remove</button>
            </div>
          ))}
        </div>
      )}
      {!open
        ? <button className="link-btn" onClick={() => setOpen(true)}>+ tailor this quest</button>
        : (
          <div className="qf-card qf-card--quiet qf-stack" style={{ marginTop: ".5rem" }}>
            <div className="hstack">
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="annotation">Audit note on an existing question</option>
                <option value="prefill">Pre-filled draft answer (to confirm)</option>
                <option value="extra_question">Extra bespoke question</option>
                <option value="hide">Hide this question, for this person only</option>
              </select>
              {anchored
                ? <select className="input" value={anchor} onChange={(e) => setAnchor(e.target.value)}>
                    {questions.map((q) => <option key={q.question_key} value={q.question_key}>{q.prompt.slice(0, 50)}…</option>)}
                  </select>
                : <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="text">Text (voice optional)</option>
                    <option value="slider">Slider</option>
                  </select>}
            </div>
            {kind === "hide" && (
              <p className="muted-sm">
                Removes “{promptOf(anchor)}” from {`this person's`} questionnaire only. The template
                and the other participants are untouched, and the progress denominator drops by one.
              </p>
            )}
            <textarea className="input" placeholder={
                kind === "annotation" ? "The audit finding the leader sees above the question…"
                : kind === "prefill" ? "A draft answer from the audit — they confirm or edit it…"
                : kind === "hide" ? "Why it's wrong for this person (optional, but worth writing down)…"
                : "The question to ask…"}
              value={text} onChange={(e) => setText(e.target.value)} />
            <div className="hstack">
              <button className="qf-btn qf-btn--primary" onClick={add}>Add</button>
              <button className="link-btn" onClick={() => setOpen(false)}>close</button>
            </div>
          </div>
        )}
    </div>
  );
}

function OrphanTailoring({ custom, questKeys, onChange }) {
  const live = new Set(questKeys);
  const orphans = (custom || []).filter((c) => !live.has(c.quest_key));
  if (!orphans.length) return null;
  return (
    <div className="qf-card">
      <div className="qf-label qf-label--accent">Tailoring on a part that no longer shows</div>
      <div className="muted-sm" style={{ marginTop: "var(--qf-3)" }}>
        {orphans.map((c) => (
          <div key={c.id} className="hstack qf-between" style={{ padding: ".3rem 0" }}>
            <span>{c.kind} · {c.quest_key} · {c.anchor_question_key || "—"}: {c.prompt_or_note}</span>
            <button className="link-btn" onClick={async () => { await api.adminUntailor(c.id); onChange(); }}>remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TranscribeButton({ id, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const run = async () => {
    setBusy(true); setNote(null);
    try {
      const r = await api.adminTranscribe({ participant_id: id });
      setNote(r.total === 0 ? "No audio" : `${r.transcribed} done${r.skipped ? `, ${r.skipped} already` : ""}${r.failed ? `, ${r.failed} failed` : ""}`);
      onDone();
    } catch (e) { setNote(e.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <span className="hstack" style={{ gap: ".5rem" }}>
      <button className="qf-btn qf-btn--ghost" onClick={run} disabled={busy}>{busy ? "Transcribing…" : "Transcribe audio"}</button>
      {note && <span className="muted-sm">{note}</span>}
    </span>
  );
}

function Shell({ children }) {
  return <div className="app-shell qf-section"><div className="qf-wrap qf-stack--lg">{children}</div></div>;
}
